import { Component, h, Prop, State, Event, EventEmitter, Watch, Element } from '@stencil/core';
import { CountryData, getCountryByCode, getFilteredCountries } from '../../../data/european-countries';

@Component({
  tag: 'country-selector',
  styleUrl: 'country-selector.css',
  shadow: false,
})
export class CountrySelector {
  @Element() el: HTMLElement;

  @Prop() selectedCode: string = '';
  @Prop() allowedCountries?: string[] | null;
  @Prop() searchPlaceholder: string = 'Search country...';
  @Prop() disabled: boolean = false;

  @State() isOpen: boolean = false;
  @State() searchText: string = '';
  @State() selectedCountry: CountryData | null = null;
  @State() filteredCountries: CountryData[] = [];
  @State() highlightedIndex: number = -1;

  @Event({
    eventName: 'countrySelected',
    bubbles: true,
    composed: true,
  })
  countrySelected: EventEmitter<CountryData>;

  private dropdownRef: HTMLDivElement;
  private searchInputRef: HTMLInputElement;
  private pendingFocus: boolean = false;

  componentWillLoad() {
    this.updateFilteredCountries();
    this.updateSelectedCountry();
  }

  componentDidLoad() {
    document.addEventListener('click', this.handleClickOutside);
  }

  componentDidRender() {
    if (this.isOpen && this.pendingFocus && this.searchInputRef) {
      this.pendingFocus = false;
      this.searchInputRef.focus();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  @Watch('selectedCode')
  watchSelectedCode() {
    this.updateSelectedCountry();
  }

  @Watch('allowedCountries')
  watchAllowedCountries() {
    this.updateFilteredCountries();
    this.updateSelectedCountry();
  }

  @Watch('searchText')
  watchSearchText() {
    this.updateFilteredCountries();
  }

  private handleClickOutside = (event: MouseEvent) => {
    // Use composedPath() instead of event.target to handle shadow DOM correctly.
    // When this component is inside a shadow DOM host (identification-component),
    // event.target gets retargeted to the shadow host, breaking contains() checks.
    const path = event.composedPath();
    if (this.dropdownRef && !path.includes(this.dropdownRef)) {
      this.isOpen = false;
      this.searchText = '';
      this.highlightedIndex = -1;
    }
  };

  private updateSelectedCountry() {
    if (this.selectedCode) {
      const country = getCountryByCode(this.selectedCode);
      if (country) {
        const allowedList = getFilteredCountries(this.allowedCountries);
        if (allowedList.some(c => c.code === country.code)) {
          this.selectedCountry = country;
        } else {
          this.selectedCountry = allowedList.length > 0 ? allowedList[0] : null;
        }
      } else {
        this.selectedCountry = null;
      }
    } else {
      this.selectedCountry = null;
    }
  }

  private updateFilteredCountries() {
    let countries = getFilteredCountries(this.allowedCountries);

    if (this.searchText.trim()) {
      const search = this.searchText.toLowerCase().trim();
      countries = countries.filter(
        country =>
          country.name.toLowerCase().includes(search) ||
          country.code.toLowerCase().includes(search) ||
          country.prefix.includes(search)
      );
    }

    this.filteredCountries = countries.sort((a, b) => a.name.localeCompare(b.name));
  }

  private toggleDropdown = () => {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.searchText = '';
      this.highlightedIndex = -1;
      this.pendingFocus = true;
    }
  };

  private selectCountry = (country: CountryData) => {
    this.selectedCountry = country;
    this.isOpen = false;
    this.searchText = '';
    this.highlightedIndex = -1;
    this.countrySelected.emit(country);
  };

  private handleSearchInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.searchText = target.value;
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    if (!this.isOpen) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        this.toggleDropdown();
      }
      return;
    }

    switch (event.key) {
      case 'Escape':
        this.isOpen = false;
        this.searchText = '';
        this.highlightedIndex = -1;
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (this.highlightedIndex < this.filteredCountries.length - 1) {
          this.highlightedIndex++;
          this.scrollToHighlighted();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.highlightedIndex > 0) {
          this.highlightedIndex--;
          this.scrollToHighlighted();
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredCountries.length) {
          this.selectCountry(this.filteredCountries[this.highlightedIndex]);
        }
        break;
      case 'Tab':
        this.isOpen = false;
        this.searchText = '';
        this.highlightedIndex = -1;
        break;
    }
  };

  private scrollToHighlighted() {
    setTimeout(() => {
      const highlighted = this.el.querySelector('.country-option.highlighted');
      if (highlighted) {
        highlighted.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 0);
  }

  render() {
    return (
      <div
        class={`country-selector ${this.disabled ? 'disabled' : ''}`}
        ref={el => (this.dropdownRef = el)}
        onKeyDown={this.handleKeyDown}
      >
        <div
          class={`country-selector-trigger ${this.isOpen ? 'open' : ''}`}
          onClick={this.toggleDropdown}
          role="button"
          tabindex={this.disabled ? -1 : 0}
          aria-haspopup="listbox"
          aria-expanded={this.isOpen}
        >
          {this.selectedCountry ? (
            <div class="selected-country">
              <span class="country-flag">{this.selectedCountry.flag}</span>
              <span class="country-prefix">{this.selectedCountry.prefix}</span>
            </div>
          ) : (
            <div class="selected-country placeholder">
              <span class="country-prefix">Select</span>
            </div>
          )}
          <span class={`dropdown-arrow ${this.isOpen ? 'open' : ''}`}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </div>

        {this.isOpen && (
          <div class="country-dropdown" role="listbox">
            <div class="country-search">
              <input
                type="text"
                placeholder={this.searchPlaceholder}
                value={this.searchText}
                onInput={this.handleSearchInput}
                ref={el => (this.searchInputRef = el)}
                class="country-search-input"
              />
            </div>

            <div class="country-list" role="listbox" aria-label="Countries">
              {this.filteredCountries.length > 0 ? (
                this.filteredCountries.map((country, index) => (
                  <div
                    key={country.code}
                    class={`country-option ${this.selectedCountry?.code === country.code ? 'selected' : ''} ${this.highlightedIndex === index ? 'highlighted' : ''}`}
                    onClick={() => this.selectCountry(country)}
                    onMouseEnter={() => { this.highlightedIndex = index; }}
                    role="option"
                    aria-selected={this.selectedCountry?.code === country.code}
                    tabindex={-1}
                  >
                    <span class="country-flag">{country.flag}</span>
                    <span class="country-prefix">{country.prefix}</span>
                    <span class="country-name">{country.name}</span>
                  </div>
                ))
              ) : (
                <div class="country-no-results">No countries found</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}
